import React, { useMemo, useState } from 'react';
import { MOODS, MoodId, getMood, pickReaction } from './moods';
import { useMood, MOOD_TAGS, MoodTag } from '../../contexts/MoodContext';
import AnimatedMoodIllustration from './visual/AnimatedMoodIllustration';
import MoodActivity from './MoodActivity';

/**
 * CheckInComposer — the playful heart of the Mood Journal.
 *
 * Tapping a floating mood is the whole interaction: it plays a reaction — the
 * illustration grows and glows, sparkles drift up, and a random, kind one-liner
 * lands with a soft entrance. From there the person can add an optional note,
 * save (one record, never overwritten — check in as often as you like), try a
 * short optional activity for that mood, or pick a different feeling. No typing
 * is ever required; everything beyond the single tap is opt-in.
 */

type Phase = 'pick' | 'react' | 'activity';

// Fixed sparkle positions (percent of the stage) so the burst is lively but
// deterministic — no layout thrash, and each has its own drift delay.
const SPARKS = [
  { x: 14, y: 28, d: 0 }, { x: 82, y: 22, d: 0.3 }, { x: 24, y: 72, d: 0.6 },
  { x: 72, y: 70, d: 0.15 }, { x: 50, y: 12, d: 0.45 }, { x: 90, y: 52, d: 0.75 },
  { x: 8, y: 54, d: 0.5 }, { x: 60, y: 88, d: 0.2 },
];

export const CheckInComposer: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const { addCheckIn, preferences } = useMood();
  const [phase, setPhase] = useState<Phase>('pick');
  const [mood, setMood] = useState<MoodId | null>(null);
  const [reaction, setReaction] = useState('');
  const [open, setOpen] = useState(false);
  const [journalEntry, setJournalEntry] = useState('');
  const [tags, setTags] = useState<MoodTag[]>([]);
  const [energy, setEnergy] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const selected = useMemo(() => getMood(mood), [mood]);

  const toggleTag = (t: MoodTag) => setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const resetToPick = () => {
    setPhase('pick');
    setMood(null);
    setReaction('');
    setOpen(false);
    setJournalEntry('');
    setTags([]);
    setEnergy(null);
    setNotes('');
  };

  // Tap a mood → play its reaction. Tapping again (or another) re-rolls the line.
  const choose = (id: MoodId) => {
    setMood(id);
    setReaction((prev) => pickReaction(id, id === mood ? prev : undefined));
    setOpen(false);
    setPhase('react');
  };

  // "Nice, continue" — persist exactly one record, then offer the optional moment.
  const saveAndContinue = () => {
    if (!mood) return;
    addCheckIn({
      mood,
      journalEntry: journalEntry || undefined,
      tags: tags.length ? tags : undefined,
      energyLevel: energy || undefined,
      notes: notes || undefined,
    });
    onSaved?.();
    setPhase('activity');
  };

  return (
    <div className="mm-composer">
      {/* ── The floating mood field (always visible so changing mood is one tap) ── */}
      <div className="mm-moodfield" role="radiogroup" aria-label="How are you feeling right now?">
        {MOODS.map((m, i) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={mood === m.id}
            aria-label={m.label}
            className={`mm-moodpick ${mood === m.id ? 'is-selected' : ''} ${mood && mood !== m.id ? 'is-dim' : ''}`}
            style={{ ['--mood-accent' as string]: m.accent, ['--mood-soft' as string]: m.soft, ['--mm-i' as string]: i }}
            onClick={() => choose(m.id)}
          >
            <span className="mm-moodpick__halo" aria-hidden="true" />
            <span className="mm-moodpick__ripple" aria-hidden="true" />
            <span className="mm-moodpick__art" aria-hidden="true">
              <AnimatedMoodIllustration mood={m.id} size="md" accent={m.accent} />
            </span>
            <span className="mm-moodpick__name">{m.label}</span>
          </button>
        ))}
      </div>

      {/* ── The reaction: big glowing illustration, sparkles, a funny kind line ── */}
      {phase === 'react' && selected && (
        <div
          className="mm-reaction mm-reaction--in"
          key={`${selected.id}-${reaction}`}
          style={{ ['--mood-accent' as string]: selected.accent, ['--mood-soft' as string]: selected.soft }}
        >
          <div className="mm-reaction__stage">
            <span className="mm-reaction__glow" aria-hidden="true" />
            {SPARKS.map((s, i) => (
              <span
                key={i}
                className="mm-reaction__spark"
                aria-hidden="true"
                style={{ left: `${s.x}%`, top: `${s.y}%`, animationDelay: `${s.d}s` }}
              />
            ))}
            <span className="mm-reaction__art">
              <AnimatedMoodIllustration mood={selected.id} size="lg" accent={selected.accent} />
            </span>
          </div>

          <p className="mm-reaction__quote" role="status">{reaction}</p>
          <p className="mm-reaction__label">Feeling {selected.label.toLowerCase()}</p>

          {!open ? (
            <button type="button" className="mm-link-btn" onClick={() => setOpen(true)}>
              + Add a note (optional)
            </button>
          ) : (
            <div className="mm-composer__details">
              <textarea
                className="mm-journal__input"
                rows={3}
                maxLength={2000}
                placeholder="A note about how you feel (optional)…"
                value={journalEntry}
                onChange={(e) => setJournalEntry(e.target.value)}
              />

              <div className="mm-field">
                <span className="mm-field__label">Tags</span>
                <div className="mm-chips">
                  {MOOD_TAGS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`mm-chip ${tags.includes(t) ? 'is-active' : ''}`}
                      aria-pressed={tags.includes(t)}
                      onClick={() => toggleTag(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mm-field">
                <span className="mm-field__label">Energy</span>
                <div className="mm-energy" role="group" aria-label="Energy level">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`mm-energy__dot ${energy && n <= energy ? 'is-on' : ''}`}
                      aria-label={`${n} of 5`}
                      aria-pressed={energy === n}
                      onClick={() => setEnergy(energy === n ? null : n)}
                    />
                  ))}
                  {energy && <span className="mm-energy__val">{energy}/5</span>}
                </div>
              </div>

              <div className="mm-field">
                <span className="mm-field__label">What influenced it?</span>
                <input
                  type="text"
                  className="mm-input"
                  maxLength={200}
                  placeholder="Optional — e.g. a good meeting, poor sleep…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          )}

          {!preferences.saveHistory && (
            <span className="mm-journal__private">History is off — this check-in won’t be saved.</span>
          )}

          <div className="mm-reaction__actions">
            <button type="button" className="mm-btn mm-btn--primary mm-btn--go" onClick={saveAndContinue}>
              Nice, continue
            </button>
            <button type="button" className="mm-link-btn" onClick={resetToPick}>
              Pick another feeling
            </button>
          </div>
        </div>
      )}

      {/* ── After saving: a saved note and one optional, easy-to-leave moment ── */}
      {phase === 'activity' && selected && (
        <div
          className="mm-reaction mm-reaction--done"
          style={{ ['--mood-accent' as string]: selected.accent, ['--mood-soft' as string]: selected.soft }}
        >
          <p className="mm-saved-note mm-saved-note--block">Check-in saved ✓</p>
          <ActivityOffer
            title={selected.activity.title}
            desc={selected.activity.desc}
            type={selected.activity.type}
            mood={selected.id}
            onDone={resetToPick}
          />
        </div>
      )}
    </div>
  );
};

/** Optional post-check-in moment. Collapsed by default; one tap to open, one to leave. */
const ActivityOffer: React.FC<{
  title: string;
  desc: string;
  type: React.ComponentProps<typeof MoodActivity>['type'];
  mood: React.ComponentProps<typeof MoodActivity>['mood'];
  onDone: () => void;
}> = ({ title, desc, type, mood, onDone }) => {
  const [playing, setPlaying] = useState(false);

  if (!playing) {
    return (
      <div className="mm-afterglow">
        <p className="mm-afterglow__lead">Want to take a short moment?</p>
        <div className="mm-reaction__actions">
          <button type="button" className="mm-btn mm-btn--primary mm-btn--go" onClick={() => setPlaying(true)}>
            {title} →
          </button>
          <button type="button" className="mm-link-btn" onClick={onDone}>No thanks, I’m done</button>
        </div>
        <p className="mm-afterglow__desc">{desc}</p>
      </div>
    );
  }

  return (
    <div className="mm-afterglow mm-afterglow--playing">
      <h3 className="mm-afterglow__title">{title}</h3>
      <div className="mm-activity"><MoodActivity type={type} mood={mood} /></div>
      <button type="button" className="mm-link-btn" onClick={onDone}>Done for now</button>
    </div>
  );
};

export default CheckInComposer;
